import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { apiFetch } from "@/lib/api";
import type { PoliticsQuestionDetail } from "@/lib/politics";
import { PoliticsQuestionCard, PoliticsSolution } from "./politics-question";
import { LoadingLine } from "./primitives";

export function PoliticsQuestionDetailPanel({
  questionId,
  onClose,
}: {
  questionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const query = useQuery({
    queryKey: ["politics", "question", questionId],
    queryFn: () => apiFetch<{ question: PoliticsQuestionDetail }>(`/politics/questions/${questionId}`),
  });
  const setFavorite = async (favorite: boolean) => {
    if (favoriteBusy) return;
    setNotice("");
    setFavoriteBusy(true);
    try {
      await apiFetch(`/politics/questions/${questionId}/favorite`, { method: favorite ? "PUT" : "DELETE" });
      queryClient.setQueryData<{ question: PoliticsQuestionDetail }>(
        ["politics", "question", questionId],
        (current) => current ? { question: { ...current.question, favorite } } : current,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["politics", "favorites"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "dashboard"] }),
      ]);
    } catch {
      setNotice("收藏状态保存失败，请稍后重试。");
    } finally {
      setFavoriteBusy(false);
    }
  };
  return (
    <div className="politics-detail-backdrop" role="presentation" onMouseDown={onClose}>
      <aside className="politics-question-detail" role="dialog" aria-modal="true" aria-label="题目详情" onMouseDown={(event) => event.stopPropagation()}>
        <button className="icon-button politics-question-detail__close" type="button" aria-label="关闭题目详情" onClick={onClose}><X /></button>
        {query.isLoading ? <LoadingLine /> : null}
        {query.isError ? <p className="politics-inline-notice">题目详情读取失败。</p> : null}
        {query.data ? (
          <>
            <PoliticsQuestionCard question={query.data.question} favoriteBusy={favoriteBusy} onFavoriteChange={(favorite) => void setFavorite(favorite)} />
            {query.data.question.solution
              ? <PoliticsSolution solution={query.data.question.solution} />
              : <p className="politics-answer-locked">完成这道题后才能查看答案与解析。</p>}
            {notice ? <p className="politics-inline-notice" role="status">{notice}</p> : null}
          </>
        ) : null}
      </aside>
    </div>
  );
}
